import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  DatePicker,
  Typography,
  Space,
  message,
  Spin,
  Select,
  Table,
  Statistic,
  Divider,
  Form,
  InputNumber,
  Modal,
  Tabs
} from 'antd';
import { DollarOutlined, FilterOutlined, BarChartOutlined, TableOutlined, PieChartOutlined } from '@ant-design/icons';
import moment from 'moment';
import Chart from 'react-apexcharts';
import { getDividendData, processDividendDataForChart } from '../services/dividendService';
import { getAllStocks, getAllAccounts, getStockInfo } from '../services/stockService';
import { getDailyHoldingsByAccount } from '../services/dashboardService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Dividend = () => {
  const [form] = Form.useForm();
  const [dividendData, setDividendData] = useState(null);
  const [chartData, setChartData] = useState({ categories: [], series: [] });
  const [loading, setLoading] = useState(false);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'table'
  const [filters, setFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthSelectValue, setMonthSelectValue] = useState(undefined);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });
  const [stockChartData, setStockChartData] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [expectedDividend, setExpectedDividend] = useState(null);

  // Function to calculate expected dividend based on current holdings
  const calculateExpectedDividend = async (stockSymbol) => {
    if (!dividendData || !dividendData.results) {
      setExpectedDividend(null);
      return;
    }

    // Filter dividend data for the selected stock
    const stockDividends = dividendData.results.filter(item => item.stock_symbol === stockSymbol);
    
    if (stockDividends.length === 0) {
      setExpectedDividend(null);
      return;
    }

    try {
      // Get actual current holdings from DailyHolding API
      const dailyHoldingsData = await getDailyHoldingsByAccount();
      
      // Calculate total quantity for this stock across all accounts
      let totalQuantity = 0;
      
      if (dailyHoldingsData && dailyHoldingsData.results) {
        dailyHoldingsData.results.forEach(holding => {
          if (holding.stock_symbol === stockSymbol) {
            totalQuantity += holding.quantity;
          }
        });
      }

      // Get the most recent dividend amount (per share)
      const sortedByDate = [...stockDividends].sort((a, b) => 
        moment(b.dividend_date).valueOf() - moment(a.dividend_date).valueOf()
      );
      const lastDividend = sortedByDate[0];
      const lastDividendAmount = parseFloat(lastDividend.dividend_amount) || 0;
      const lastDividendDate = moment(lastDividend.dividend_date);

      // Calculate expected dividend
      const expectedAmount = totalQuantity * lastDividendAmount;

      // Calculate last received dividend (월별 합산 - dividend timeline과 동일한 방식)
      // Group by month and get the most recent month's data
      const monthlyGroups = {};
      stockDividends.forEach(item => {
        const monthKey = moment(item.dividend_date).format('YYYY-MM');
        if (!monthlyGroups[monthKey]) {
          monthlyGroups[monthKey] = {
            month: monthKey,
            dividends: [],
            totalQuantity: 0,
            totalAmount: 0,
            perShareAmount: parseFloat(item.dividend_amount) || 0,
            accounts: new Set()
          };
        }
        
        const quantity = parseInt(item.quantity) || 0;
        const perShare = parseFloat(item.dividend_amount) || 0;
        const totalForRecord = perShare * quantity;
        
        monthlyGroups[monthKey].totalQuantity += quantity;
        monthlyGroups[monthKey].totalAmount += totalForRecord;
        monthlyGroups[monthKey].dividends.push(item);
        monthlyGroups[monthKey].accounts.add(item.account_name || 'Unknown');
      });

      // Get the most recent month
      const sortedMonths = Object.keys(monthlyGroups).sort().reverse();
      const lastMonth = sortedMonths[0];
      const lastMonthData = monthlyGroups[lastMonth];
      
      // Get accounts list
      const accountsList = Array.from(lastMonthData.accounts).join(', ');

      setExpectedDividend({
        stockSymbol: stockSymbol,
        stockName: lastDividend.stock_name,
        totalQuantity: totalQuantity,
        lastDividendAmount: lastDividendAmount,
        lastDividendDate: lastDividendDate.format('YYYY-MM-DD'),
        expectedAmount: expectedAmount,
        // Quantity change calculation
        quantityChange: totalQuantity - lastMonthData.totalQuantity,
        // Last received dividend information (월별 합산)
        lastReceivedQuantity: lastMonthData.totalQuantity,
        lastReceivedTotalAmount: lastMonthData.totalAmount,
        lastReceivedMonth: lastMonth,
        lastReceivedDate: lastMonth, // 월 단위로 표시
        lastReceivedAccount: accountsList,
        lastReceivedRecordCount: lastMonthData.dividends.length
      });
    } catch (error) {
      console.error('Failed to fetch daily holdings for expected dividend calculation:', error);
      message.warning('실제 보유 수량을 가져오는데 실패했습니다.');
      setExpectedDividend(null);
    }
  };

  // Function to fetch stock price data for specific dates
  const fetchStockPriceData = async (stockSymbol, dates) => {
    try {
      if (!dates || dates.length === 0) return {};
      
      // Get date range
      const sortedDates = [...dates].sort();
      const startDate = moment(sortedDates[0]).subtract(5, 'days').format('YYYY-MM-DD'); // Add buffer for weekends
      const endDate = moment(sortedDates[sortedDates.length - 1]).add(5, 'days').format('YYYY-MM-DD');
      
      // Fetch stock price data
      const response = await getStockInfo(stockSymbol, startDate, endDate);
      
      if (response && Array.isArray(response)) {
        // Create a map of date -> closing price
        const priceMap = {};
        response.forEach(item => {
          if (item.date && item.close) {
            const dateKey = moment(item.date).format('YYYY-MM-DD');
            priceMap[dateKey] = parseFloat(item.close);
          }
        });
        
        // For each dividend date, find the closest stock price
        const result = {};
        dates.forEach(date => {
          const targetDate = moment(date);
          let closestPrice = null;
          let minDiff = Infinity;
          
          // Look for exact match first
          if (priceMap[date]) {
            result[date] = priceMap[date];
            return;
          }
          
          // If no exact match, find closest date within 7 days
          Object.keys(priceMap).forEach(priceDate => {
            const diff = Math.abs(targetDate.diff(moment(priceDate), 'days'));
            if (diff <= 7 && diff < minDiff) {
              minDiff = diff;
              closestPrice = priceMap[priceDate];
            }
          });
          
          result[date] = closestPrice;
        });
        
        return result;
      }
      
      return {};
    } catch (error) {
      console.error('Failed to fetch stock price data:', error);
      message.warning('Failed to fetch stock price data. Chart will show without stock prices.');
      return {};
    }
  };

  const renderStockDividendChart = async (stockData) => {
    if (!stockData || stockData.length === 0) {
      setStockChartData(null);
      return;
    }

    // Group data by date and aggregate
    const dateGroups = {};
    stockData.forEach(item => {
      const date = moment(item.dividend_date).format('YYYY-MM-DD');
      if (!dateGroups[date]) {
        dateGroups[date] = {
          date: date,
          totalQuantity: 0,
          totalAmount: 0,
          perShareAmount: parseFloat(item.dividend_amount) || 0, // Use first per share amount
          records: []
        };
      }
      
      const quantity = parseInt(item.quantity) || 0;
      const perShare = parseFloat(item.dividend_amount) || 0;
      const totalForRecord = perShare * quantity;
      
      dateGroups[date].totalQuantity += quantity;
      dateGroups[date].totalAmount += totalForRecord;
      dateGroups[date].records.push(item);
    });

    // Convert to sorted array
    const sortedData = Object.values(dateGroups).sort((a, b) => 
      moment(a.date).valueOf() - moment(b.date).valueOf()
    );

    // Fetch stock price data for dividend dates
    const stockSymbol = stockData[0].stock_symbol;
    const stockPriceData = await fetchStockPriceData(stockSymbol, sortedData.map(item => item.date));

    // Prepare chart data
    const categories = sortedData.map(item => item.date);
    const quantityData = sortedData.map(item => item.totalQuantity);
    const perShareData = sortedData.map(item => item.perShareAmount);
    const totalAmountData = sortedData.map(item => item.totalAmount);
    const stockPrices = categories.map(date => stockPriceData[date] || null);

    const chartConfig = {
      chart: {
        height: 400,
        type: 'line',
        stacked: false,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: [3, 3, 3, 2],
        curve: 'smooth'
      },
      markers: {
        size: 6,
        hover: {
          size: 8
        }
      },
      title: {
        text: `Dividend Timeline - ${sortedData[0]?.records[0]?.stock_name} (${sortedData[0]?.records[0]?.stock_symbol})`,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#1890ff'
        }
      },
      xaxis: {
        categories: categories,
        title: {
          text: 'Dividend Date'
        },
        labels: {
          rotate: -45,
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: [
        {
          title: {
            text: "Quantity",
            style: {
              color: '#722ed1',
            }
          },
          labels: {
            style: {
              colors: '#722ed1',
            },
            formatter: function (val) {
              return val ? val.toLocaleString() : '0';
            }
          }
        },
        {
          opposite: true,
          title: {
            text: "Per Share Amount (₩)",
            style: {
              color: '#1890ff',
            }
          },
          labels: {
            style: {
              colors: '#1890ff',
            },
            formatter: function (val) {
              return val ? '₩' + val.toLocaleString() : '₩0';
            }
          }
        },
        {
          opposite: true,
          title: {
            text: "Total Amount (₩)",
            style: {
              color: '#52c41a',
            }
          },
          labels: {
            style: {
              colors: '#52c41a',
            },
            formatter: function (val) {
              return val ? '₩' + val.toLocaleString() : '₩0';
            }
          }
        },
        {
          opposite: true,
          title: {
            text: "Stock Price (₩)",
            style: {
              color: '#ff7300',
            }
          },
          labels: {
            style: {
              colors: '#ff7300',
            },
            formatter: function (val) {
              return val ? '₩' + val.toLocaleString() : '₩0';
            }
          }
        }
      ],
      tooltip: {
        shared: true,
        intersect: false,
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const date = categories[dataPointIndex];
          const quantity = quantityData[dataPointIndex];
          const perShare = perShareData[dataPointIndex];
          const totalAmount = totalAmountData[dataPointIndex];
          const stockPrice = stockPrices[dataPointIndex];
          
          return `
            <div style="padding: 12px; background: white; border: 1px solid #e8e8e8; border-radius: 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
              <div style="font-weight: bold; margin-bottom: 8px; color: #333;">${date}</div>
              <div style="margin-bottom: 4px;">
                <span style="color: #722ed1;">● </span>
                <strong>Quantity: ${quantity.toLocaleString()}</strong>
              </div>
              <div style="margin-bottom: 4px;">
                <span style="color: #1890ff;">● </span>
                <strong>Per Share: ₩${perShare.toLocaleString()}</strong>
              </div>
              <div style="margin-bottom: 4px;">
                <span style="color: #52c41a;">● </span>
                <strong>Total Amount: ₩${totalAmount.toLocaleString()}</strong>
              </div>
              ${stockPrice ? `<div style="margin-bottom: 4px;">
                <span style="color: #ff7300;">● </span>
                <strong>Stock Price: ₩${stockPrice.toLocaleString()}</strong>
              </div>` : ''}
            </div>
          `;
        }
      },
      legend: {
        horizontalAlign: 'left',
        offsetX: 40
      },
      colors: ['#722ed1', '#1890ff', '#52c41a', '#ff7300'],
      grid: {
        borderColor: '#f0f0f0',
        strokeDashArray: 3
      }
    };

    const series = [
      {
        name: 'Quantity',
        type: 'line',
        yAxisIndex: 0,
        data: quantityData
      },
      {
        name: 'Per Share Amount',
        type: 'line',
        yAxisIndex: 1,
        data: perShareData
      },
      {
        name: 'Total Amount',
        type: 'line',
        yAxisIndex: 2,
        data: totalAmountData
      },
      {
        name: 'Stock Price',
        type: 'line',
        yAxisIndex: 3,
        data: stockPrices
      }
    ];

    setStockChartData({ options: chartConfig, series: series });
    setSelectedStock(sortedData[0]?.stock_symbol);
  };

  // Load initial data
  useEffect(() => {
    loadDividendData();
    loadStocks();
    loadAccounts();
  }, []);

  const loadStocks = async () => {
    setStocksLoading(true);
    try {
      const data = await getAllStocks();
      setStocks(data.results || []);
    } catch (error) {
      console.error('Failed to fetch stocks', error);
      message.error('Failed to fetch stock list. Please try again.');
    } finally {
      setStocksLoading(false);
    }
  };

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const data = await getAllAccounts();
      setAccounts(data.results || []);
    } catch (error) {
      console.error('Failed to fetch accounts', error);
      message.error('Failed to fetch account list. Please try again.');
    } finally {
      setAccountsLoading(false);
    }
  };

  const loadDividendData = async (filterParams = {}) => {
    setLoading(true);
    try {
      const data = await getDividendData(filterParams);
      setDividendData(data);
      
      const processedData = processDividendDataForChart(data);
      setChartData(processedData);
      
      message.success(`Loaded ${data.count} dividend records`);
    } catch (error) {
      console.error('Failed to fetch dividend data', error);
      message.error('Failed to fetch dividend data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (values) => {
    const filterParams = {};
    
    // Process form values
    if (values.dateRange && values.dateRange.length === 2) {
      filterParams.date_from = values.dateRange[0].format('YYYY-MM-DD');
      filterParams.date_to = values.dateRange[1].format('YYYY-MM-DD');
    }
    
    // Handle annual filter
    if (values.year) {
      filterParams.date_from = `${values.year}-01-01`;
      filterParams.date_to = `${values.year}-12-31`;
    }
    
    if (values.account_name) {
      filterParams.account_name = values.account_name;
    }
    
    if (values.stock_name) {
      filterParams.stock_name = values.stock_name;
    }
    
    if (values.min_amount) {
      filterParams.min_amount = values.min_amount;
    }
    
    if (values.max_amount) {
      filterParams.max_amount = values.max_amount;
    }

    setFilters(filterParams);
    loadDividendData(filterParams);
  };

  const handleClearFilters = () => {
    form.resetFields();
    setFilters({});
    loadDividendData();
  };

  // Chart configuration
  const chartOptions = {
    chart: {
      height: 350,
      type: 'line',
      stacked: false,
      toolbar: {
        show: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      width: [3, 2],
      curve: 'smooth'
    },
    markers: {
      size: 4,
      hover: {
        size: 6
      }
    },
    title: {
      text: 'Monthly Dividend Analysis',
      align: 'left',
      offsetX: 110
    },
    xaxis: {
      categories: chartData.categories || [],
      title: {
        text: 'Month'
      }
    },
    yaxis: [
      {
        title: {
          text: "Total Amount (₩)",
          style: {
            color: '#008FFB',
          }
        },
        labels: {
          style: {
            colors: '#008FFB',
          },
          formatter: function (val) {
            return val ? '₩' + val.toLocaleString() : '₩0';
          }
        }
      },
      {
        opposite: true,
        title: {
          text: "Per Share Amount (₩)",
          style: {
            color: '#00E396',
          }
        },
        labels: {
          style: {
            colors: '#00E396',
          },
          formatter: function (val) {
            return val ? '₩' + val.toLocaleString() : '₩0';
          }
        }
      }
    ],
    tooltip: {
      shared: true,
      intersect: false,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        try {
          const monthKey = chartData.categories && chartData.categories[dataPointIndex];
          const monthlyDetail = monthKey && chartData.monthlyDetails && chartData.monthlyDetails[monthKey];
          
          if (!monthlyDetail) {
            return '<div class="apexcharts-tooltip-custom">No data available</div>';
          }

          let tooltipContent = `
            <div class="apexcharts-tooltip-custom" style="padding: 12px; background: white; border: 1px solid #e8e8e8; border-radius: 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
              <div style="font-weight: bold; margin-bottom: 8px; color: #333;">${monthKey}</div>
              <div style="margin-bottom: 8px;">
                <span style="color: #008FFB;">● </span>
                <strong>Total Dividend Amount: ₩${monthlyDetail.totalAmount.toLocaleString()}</strong>
              </div>
          `;

          // Add month-over-month comparison
          if (monthlyDetail.comparison && dataPointIndex > 0) {
            const { totalAmountChange, totalAmountChangePercent, previousMonthTotalAmount } = monthlyDetail.comparison;
            const isPositive = totalAmountChange >= 0;
            const changeColor = isPositive ? '#52c41a' : '#ff4d4f';
            const changeSymbol = isPositive ? '+' : '';
            const arrowSymbol = isPositive ? '↗' : '↘';
            
            tooltipContent += `
              <div style="margin-bottom: 8px; padding: 6px; background-color: ${isPositive ? '#f6ffed' : '#fff2f0'}; border-radius: 4px; border-left: 3px solid ${changeColor};">
                <div style="font-size: 12px; color: #666; margin-bottom: 2px;">vs Previous Month:</div>
                <div style="color: ${changeColor}; font-weight: bold;">
                  ${arrowSymbol} ${changeSymbol}₩${Math.abs(totalAmountChange).toLocaleString()} (${changeSymbol}${totalAmountChangePercent.toFixed(1)}%)
                </div>
                <div style="font-size: 11px; color: #999; margin-top: 2px;">
                  Previous: ₩${previousMonthTotalAmount.toLocaleString()}
                </div>
              </div>
            `;
          } else if (dataPointIndex === 0) {
            tooltipContent += `
              <div style="margin-bottom: 8px; padding: 6px; background-color: #f0f0f0; border-radius: 4px;">
                <div style="font-size: 12px; color: #666;">
                  📊 First month in data range
                </div>
              </div>
            `;
          }

          // Add account breakdown
          if (monthlyDetail.accounts && Object.keys(monthlyDetail.accounts).length > 0) {
            tooltipContent += '<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee;"><strong>By Account:</strong></div>';
            Object.entries(monthlyDetail.accounts).forEach(([accountName, accountData]) => {
              tooltipContent += `
                <div style="margin-left: 8px; margin-top: 4px;">
                  <span style="color: #666;">• ${accountName}: ₩${accountData.totalAmount.toLocaleString()}</span>
                </div>
              `;
            });
          }

          tooltipContent += '</div>';
          return tooltipContent;
        } catch (error) {
          console.error('Tooltip error:', error);
          return '<div class="apexcharts-tooltip-custom">Error loading data</div>';
        }
      }
    },
    legend: {
      horizontalAlign: 'left',
      offsetX: 40
    },
    colors: ['#008FFB', '#00E396']
  };

  // Table columns
  const tableColumns = [
    {
      title: 'Date',
      dataIndex: 'dividend_date',
      key: 'dividend_date',
      sorter: (a, b) => moment(a.dividend_date).unix() - moment(b.dividend_date).unix(),
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'Stock Symbol',
      dataIndex: 'stock_symbol',
      key: 'stock_symbol',
      filters: dividendData ? [...new Set(dividendData.results.map(item => item.stock_symbol))].map(symbol => ({ text: symbol, value: symbol })) : [],
      onFilter: (value, record) => record.stock_symbol === value,
    },
    {
      title: 'Stock Name',
      dataIndex: 'stock_name',
      key: 'stock_name',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (quantity) => quantity.toLocaleString()
    },
    {
      title: 'Dividend Amount (Per Share)',
      dataIndex: 'dividend_amount',
      key: 'dividend_amount',
      sorter: (a, b) => parseFloat(a.dividend_amount) - parseFloat(b.dividend_amount),
      render: (amount) => `₩${parseFloat(amount).toLocaleString('ko-KR')}`
    },
    {
      title: 'Total Amount',
      key: 'total_amount',
      sorter: (a, b) => (parseFloat(a.dividend_amount) * parseInt(a.quantity)) - (parseFloat(b.dividend_amount) * parseInt(b.quantity)),
      render: (_, record) => {
        const totalAmount = parseFloat(record.dividend_amount) * parseInt(record.quantity);
        return `₩${totalAmount.toLocaleString('ko-KR')}`;
      }
    },
    {
      title: 'Account',
      dataIndex: 'account_name',
      key: 'account_name',
    }
  ];

  // Calculate statistics with safety checks
  const totalDividends = dividendData && dividendData.results && Array.isArray(dividendData.results) 
    ? dividendData.results.reduce((sum, item) => {
        const amount = parseFloat(item?.dividend_amount) || 0;
        return sum + amount;
      }, 0) 
    : 0;
    
  const totalDividendAmount = dividendData && dividendData.results && Array.isArray(dividendData.results)
    ? dividendData.results.reduce((sum, item) => {
        const amount = parseFloat(item?.dividend_amount) || 0;
        const quantity = parseInt(item?.quantity) || 0;
        return sum + (amount * quantity);
      }, 0)
    : 0;
    
  const averageDividend = dividendData && dividendData.count > 0 ? totalDividends / dividendData.count : 0;
  const averageTotalDividend = dividendData && dividendData.count > 0 ? totalDividendAmount / dividendData.count : 0;
  const uniqueStocks = dividendData && dividendData.results && Array.isArray(dividendData.results)
    ? new Set(dividendData.results.map(item => item?.stock_symbol).filter(Boolean)).size 
    : 0;

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* Header */}
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <DollarOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#52c41a' }} />
                <Title level={2} style={{ display: 'inline', margin: 0 }}>
                  Dividend Analysis
                </Title>
              </div>
              
              {/* Statistics */}
              <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Dividend Amount"
                    value={totalDividendAmount}
                    precision={0}
                    prefix="₩"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Average Total Dividend"
                    value={averageTotalDividend}
                    precision={0}
                    prefix="₩"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Records"
                    value={dividendData ? dividendData.count : 0}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Unique Stocks"
                    value={uniqueStocks}
                    valueStyle={{ color: '#eb2f96' }}
                  />
                </Col>
              </Row>
              
              {/* Additional Statistics Row */}
              <Row gutter={16} style={{ marginTop: '16px' }}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Dividends (Per Share)"
                    value={totalDividends}
                    precision={0}
                    prefix="₩"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Average Dividend (Per Share)"
                    value={averageDividend}
                    precision={0}
                    prefix="₩"
                    valueStyle={{ color: '#13c2c2' }}
                  />
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>

        {/* Filters */}
        <Col span={24}>
          <Card title={<><FilterOutlined /> Filters</>} size="small">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFilterSubmit}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="dateRange" label="Date Range">
                    <RangePicker
                      format="YYYY-MM-DD"
                      style={{ width: '100%' }}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Space size="small">
                        <Button 
                          size="small" 
                          onClick={() => form.setFieldsValue({ 
                            dateRange: [moment().subtract(1, 'month'), moment()],
                            year: undefined 
                          })}
                        >
                          1M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => form.setFieldsValue({ 
                            dateRange: [moment().subtract(3, 'months'), moment()],
                            year: undefined 
                          })}
                        >
                          3M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => form.setFieldsValue({ 
                            dateRange: [moment().subtract(6, 'months'), moment()],
                            year: undefined 
                          })}
                        >
                          6M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => form.setFieldsValue({ 
                            dateRange: [moment().subtract(1, 'year'), moment()],
                            year: undefined 
                          })}
                        >
                          1Y
                        </Button>
                      </Space>
                    </div>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="year" label="Year">
                    <Select
                      placeholder="Select year"
                      style={{ width: '100%' }}
                      allowClear
                    >
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <Option key={year} value={year}>
                            {year}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="account_name" label="Account Names">
                    <Select
                      mode="multiple"
                      placeholder="Select account names"
                      style={{ width: '100%' }}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      loading={accountsLoading}
                      notFoundContent={accountsLoading ? <Spin size="small" /> : 'No accounts found'}
                    >
                      {accounts.map(account => (
                        <Option key={account.id} value={account.account_name} label={account.account_name}>
                          {account.account_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="stock_name" label="Stock Names">
                    <Select
                      mode="multiple"
                      placeholder="Select stock names"
                      style={{ width: '100%' }}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      loading={stocksLoading}
                      notFoundContent={stocksLoading ? <Spin size="small" /> : 'No stocks found'}
                    >
                      {stocks.map(stock => (
                        <Option
                          key={stock.id}
                          value={stock.stock_name}
                          label={`${stock.stock_name} (${stock.stock_symbol})`}
                        >
                          {stock.stock_name} ({stock.stock_symbol})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="min_amount" label="Min Amount">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0"
                      min={0}
                      step={1000}
                      prefix="₩"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="max_amount" label="Max Amount">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="1000000"
                      min={0}
                      step={1000}
                      prefix="₩"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label=" " style={{ marginBottom: 0 }}>
                    <Space>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Apply Filters
                      </Button>
                      <Button onClick={handleClearFilters}>
                        Clear
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        {/* View Mode Toggle */}
        <Col span={24}>
          <Card size="small">
            <Space>
              <Text strong>View Mode:</Text>
              <Button.Group>
                <Button
                  type={viewMode === 'chart' ? 'primary' : 'default'}
                  icon={<BarChartOutlined />}
                  onClick={() => setViewMode('chart')}
                >
                  Chart
                </Button>
                <Button
                  type={viewMode === 'table' ? 'primary' : 'default'}
                  icon={<TableOutlined />}
                  onClick={() => setViewMode('table')}
                >
                  Table
                </Button>
              </Button.Group>
            </Space>
          </Card>
        </Col>

        {/* Content */}
        <Col span={24}>
          {loading ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text>Loading dividend data...</Text>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              {viewMode === 'chart' ? (
                <div>
                  {chartData.series && chartData.series.length > 0 && chartData.categories && chartData.categories.length > 0 ? (
                    <div>
                      <div key={`chart-${chartData.categories.length}-${chartData.series.length}`}>
                        <Chart
                          options={chartOptions}
                          series={chartData.series}
                          type="line"
                          height={350}
                          key={`apexchart-${Date.now()}`}
                        />
                      </div>
                      
                      {/* Month selector for detailed view */}
                      <div style={{ marginTop: '16px', textAlign: 'center' }}>
                        <Space>
                          <Text strong>View detailed breakdown for month:</Text>
                          <Select
                            placeholder="Select a month"
                            style={{ width: 200 }}
                            value={monthSelectValue}
                            onChange={(monthKey) => {
                              setMonthSelectValue(monthKey);
                              if (monthKey && dividendData && dividendData.results) {
                                const monthData = dividendData.results.filter(item => {
                                  const date = new Date(item.dividend_date);
                                  const itemMonthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                  return itemMonthKey === monthKey;
                                });
                                setSelectedMonth(monthKey);
                                setSelectedMonthData(monthData);
                                setModalVisible(true);
                              }
                            }}
                          >
                            {[...chartData.categories].reverse().map(monthKey => (
                              <Option key={monthKey} value={monthKey}>
                                {monthKey}
                              </Option>
                            ))}
                          </Select>
                        </Space>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                      <Text type="secondary">No data available for chart</Text>
                    </div>
                  )}
                </div>
              ) : (
                <Tabs
                  defaultActiveKey="all"
                  items={[
                    {
                      key: 'all',
                      label: '전체',
                      children: (
                        <Table
                          columns={tableColumns}
                          dataSource={dividendData ? dividendData.results : []}
                          rowKey="id"
                          pagination={{
                            ...pagination,
                            total: dividendData ? dividendData.count : 0,
                          }}
                          onChange={(paginationInfo) => {
                            setPagination({
                              ...pagination,
                              current: paginationInfo.current,
                              pageSize: paginationInfo.pageSize,
                            });
                          }}
                          scroll={{ x: 800 }}
                        />
                      )
                    },
                    {
                      key: 'monthly',
                      label: '월별',
                      children: (() => {
                        // Group data by month
                        const monthlyData = {};
                        if (dividendData && dividendData.results) {
                          dividendData.results.forEach(item => {
                            const date = moment(item.dividend_date);
                            const monthKey = date.format('YYYY-MM');
                            
                            if (!monthlyData[monthKey]) {
                              monthlyData[monthKey] = {
                                month: monthKey,
                                displayMonth: date.format('YYYY년 MM월'),
                                records: [],
                                totalAmount: 0,
                                totalQuantity: 0,
                                uniqueStocks: new Set()
                              };
                            }
                            
                            const totalAmount = parseFloat(item.dividend_amount) * parseInt(item.quantity);
                            monthlyData[monthKey].records.push(item);
                            monthlyData[monthKey].totalAmount += totalAmount;
                            monthlyData[monthKey].totalQuantity += parseInt(item.quantity);
                            monthlyData[monthKey].uniqueStocks.add(item.stock_symbol);
                          });
                        }
                        
                        // Convert to array and sort by month (descending)
                        const monthlyArray = Object.values(monthlyData)
                          .sort((a, b) => moment(b.month).valueOf() - moment(a.month).valueOf());
                        
                        // Calculate month-over-month changes
                        monthlyArray.forEach((item, index) => {
                          if (index < monthlyArray.length - 1) {
                            const prevMonth = monthlyArray[index + 1];
                            item.changeAmount = item.totalAmount - prevMonth.totalAmount;
                            item.changePercent = prevMonth.totalAmount > 0 
                              ? ((item.totalAmount - prevMonth.totalAmount) / prevMonth.totalAmount) * 100 
                              : 0;
                          } else {
                            item.changeAmount = null;
                            item.changePercent = null;
                          }
                        });
                        
                        const monthlyColumns = [
                          {
                            title: '월',
                            dataIndex: 'displayMonth',
                            key: 'displayMonth',
                            width: 150,
                            render: (text) => <Text strong style={{ fontSize: '16px' }}>{text}</Text>
                          },
                          {
                            title: '배당 건수',
                            dataIndex: 'records',
                            key: 'recordCount',
                            width: 100,
                            align: 'center',
                            render: (records) => records.length
                          },
                          {
                            title: '총 수량',
                            dataIndex: 'totalQuantity',
                            key: 'totalQuantity',
                            width: 100,
                            align: 'right',
                            render: (quantity) => quantity.toLocaleString()
                          },
                          {
                            title: '종목 수',
                            dataIndex: 'uniqueStocks',
                            key: 'uniqueStocks',
                            width: 100,
                            align: 'center',
                            render: (stocks) => stocks.size
                          },
                          {
                            title: '총 배당금',
                            dataIndex: 'totalAmount',
                            key: 'totalAmount',
                            width: 150,
                            align: 'right',
                            render: (amount) => (
                              <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                                ₩{amount.toLocaleString()}
                              </Text>
                            )
                          },
                          {
                            title: '전월 대비',
                            key: 'change',
                            width: 180,
                            align: 'right',
                            render: (_, record) => {
                              if (record.changeAmount === null) {
                                return <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>;
                              }
                              
                              const isPositive = record.changeAmount >= 0;
                              const color = isPositive ? '#52c41a' : '#ff4d4f';
                              const arrow = isPositive ? '↑' : '↓';
                              
                              return (
                                <div>
                                  <div style={{ color, fontWeight: 'bold', fontSize: '14px' }}>
                                    {arrow} {isPositive ? '+' : ''}₩{Math.abs(record.changeAmount).toLocaleString()}
                                  </div>
                                  <Text style={{ color, fontSize: '12px' }}>
                                    ({isPositive ? '+' : ''}{record.changePercent.toFixed(1)}%)
                                  </Text>
                                </div>
                              );
                            }
                          }
                        ];
                        
                        return (
                          <Table
                            columns={monthlyColumns}
                            dataSource={monthlyArray}
                            rowKey="month"
                            pagination={{ pageSize: 12 }}
                            expandable={{
                              expandedRowRender: (record) => (
                                <div style={{ margin: 0, padding: '16px', backgroundColor: '#fafafa' }}>
                                  <Title level={5} style={{ marginBottom: '16px' }}>
                                    {record.displayMonth} 상세 내역
                                  </Title>
                                  <Table
                                    columns={tableColumns}
                                    dataSource={record.records}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 800 }}
                                  />
                                </div>
                              )
                            }}
                          />
                        );
                      })()
                    },
                    {
                      key: 'yearly',
                      label: '년도별',
                      children: (() => {
                        // Group data by year
                        const yearlyData = {};
                        if (dividendData && dividendData.results) {
                          dividendData.results.forEach(item => {
                            const date = moment(item.dividend_date);
                            const yearKey = date.format('YYYY');
                            
                            if (!yearlyData[yearKey]) {
                              yearlyData[yearKey] = {
                                year: yearKey,
                                displayYear: `${yearKey}년`,
                                records: [],
                                totalAmount: 0,
                                totalQuantity: 0,
                                uniqueStocks: new Set(),
                                monthlyBreakdown: {}
                              };
                            }
                            
                            const totalAmount = parseFloat(item.dividend_amount) * parseInt(item.quantity);
                            yearlyData[yearKey].records.push(item);
                            yearlyData[yearKey].totalAmount += totalAmount;
                            yearlyData[yearKey].totalQuantity += parseInt(item.quantity);
                            yearlyData[yearKey].uniqueStocks.add(item.stock_symbol);
                            
                            // Monthly breakdown within year
                            const monthKey = date.format('MM');
                            if (!yearlyData[yearKey].monthlyBreakdown[monthKey]) {
                              yearlyData[yearKey].monthlyBreakdown[monthKey] = {
                                month: monthKey,
                                displayMonth: `${monthKey}월`,
                                amount: 0
                              };
                            }
                            yearlyData[yearKey].monthlyBreakdown[monthKey].amount += totalAmount;
                          });
                        }
                        
                        // Convert to array and sort by year (descending)
                        const yearlyArray = Object.values(yearlyData)
                          .sort((a, b) => parseInt(b.year) - parseInt(a.year));
                        
                        // Calculate year-over-year changes
                        yearlyArray.forEach((item, index) => {
                          if (index < yearlyArray.length - 1) {
                            const prevYear = yearlyArray[index + 1];
                            item.changeAmount = item.totalAmount - prevYear.totalAmount;
                            item.changePercent = prevYear.totalAmount > 0 
                              ? ((item.totalAmount - prevYear.totalAmount) / prevYear.totalAmount) * 100 
                              : 0;
                          } else {
                            item.changeAmount = null;
                            item.changePercent = null;
                          }
                        });
                        
                        const yearlyColumns = [
                          {
                            title: '년도',
                            dataIndex: 'displayYear',
                            key: 'displayYear',
                            width: 120,
                            render: (text) => <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>{text}</Text>
                          },
                          {
                            title: '배당 건수',
                            dataIndex: 'records',
                            key: 'recordCount',
                            width: 100,
                            align: 'center',
                            render: (records) => <Text style={{ fontSize: '16px' }}>{records.length}</Text>
                          },
                          {
                            title: '총 수량',
                            dataIndex: 'totalQuantity',
                            key: 'totalQuantity',
                            width: 120,
                            align: 'right',
                            render: (quantity) => <Text style={{ fontSize: '16px' }}>{quantity.toLocaleString()}</Text>
                          },
                          {
                            title: '종목 수',
                            dataIndex: 'uniqueStocks',
                            key: 'uniqueStocks',
                            width: 100,
                            align: 'center',
                            render: (stocks) => <Text style={{ fontSize: '16px' }}>{stocks.size}</Text>
                          },
                          {
                            title: '총 배당금',
                            dataIndex: 'totalAmount',
                            key: 'totalAmount',
                            width: 160,
                            align: 'right',
                            render: (amount) => (
                              <Text strong style={{ color: '#52c41a', fontSize: '18px' }}>
                                ₩{amount.toLocaleString()}
                              </Text>
                            )
                          },
                          {
                            title: '전년 대비',
                            key: 'change',
                            width: 200,
                            align: 'right',
                            render: (_, record) => {
                              if (record.changeAmount === null) {
                                return <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>;
                              }
                              
                              const isPositive = record.changeAmount >= 0;
                              const color = isPositive ? '#52c41a' : '#ff4d4f';
                              const arrow = isPositive ? '↑' : '↓';
                              
                              return (
                                <div>
                                  <div style={{ color, fontWeight: 'bold', fontSize: '16px' }}>
                                    {arrow} {isPositive ? '+' : ''}₩{Math.abs(record.changeAmount).toLocaleString()}
                                  </div>
                                  <Text style={{ color, fontSize: '13px' }}>
                                    ({isPositive ? '+' : ''}{record.changePercent.toFixed(1)}%)
                                  </Text>
                                </div>
                              );
                            }
                          }
                        ];
                        
                        return (
                          <Table
                            columns={yearlyColumns}
                            dataSource={yearlyArray}
                            rowKey="year"
                            pagination={false}
                            expandable={{
                              expandedRowRender: (record) => (
                                <div style={{ margin: 0, padding: '16px', backgroundColor: '#fafafa' }}>
                                  <Title level={5} style={{ marginBottom: '16px' }}>
                                    {record.displayYear} 월별 현황
                                  </Title>
                                  <Row gutter={16} style={{ marginBottom: '24px' }}>
                                    {Object.values(record.monthlyBreakdown)
                                      .sort((a, b) => parseInt(a.month) - parseInt(b.month))
                                      .map(monthData => (
                                        <Col key={monthData.month} xs={12} sm={8} md={6} lg={4}>
                                          <Card size="small">
                                            <Statistic
                                              title={monthData.displayMonth}
                                              value={monthData.amount}
                                              precision={0}
                                              prefix="₩"
                                              valueStyle={{ fontSize: '14px', color: '#52c41a' }}
                                            />
                                          </Card>
                                        </Col>
                                      ))
                                    }
                                  </Row>
                                  <Divider />
                                  <Title level={5} style={{ marginBottom: '16px' }}>
                                    {record.displayYear} 상세 내역
                                  </Title>
                                  <Table
                                    columns={tableColumns}
                                    dataSource={record.records}
                                    rowKey="id"
                                    pagination={{ pageSize: 20 }}
                                    size="small"
                                    scroll={{ x: 800 }}
                                  />
                                </div>
                              )
                            }}
                          />
                        );
                      })()
                    }
                  ]}
                />
              )}
            </Card>
          )}
        </Col>

        {/* Stock-specific Dividend Timeline */}
        <Col span={24}>
          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                <BarChartOutlined style={{ marginRight: '8px' }} />
                Stock-specific Dividend Timeline
              </Title>
              <Text type="secondary">
                Select a stock to view its dividend history over time
              </Text>
            </div>
            
            {/* Stock selector */}
            <div style={{ marginBottom: '24px' }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space align="start">
                  <Text strong>Select Stock:</Text>
                  <Select
                    placeholder="Choose a stock to view dividend timeline"
                    style={{ width: 300 }}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(stockSymbol) => {
                      if (stockSymbol && dividendData && dividendData.results) {
                        const stockData = dividendData.results.filter(item => item.stock_symbol === stockSymbol);
                        if (stockData.length > 0) {
                          renderStockDividendChart(stockData);
                          calculateExpectedDividend(stockSymbol);
                        }
                      }
                    }}
                  >
                    {dividendData && dividendData.results ? 
                      [...new Set(dividendData.results.map(item => item.stock_symbol))]
                        .filter(Boolean)
                        .sort()
                        .map(symbol => {
                          const stockName = dividendData.results.find(item => item.stock_symbol === symbol)?.stock_name;
                          const displayText = `${stockName} (${symbol})`;
                          return (
                            <Option key={symbol} value={symbol} label={displayText}>
                              {displayText}
                            </Option>
                          );
                        })
                      : []
                    }
                  </Select>
                </Space>
                
                {/* Expected Dividend and Last Received Dividend Display */}
                {expectedDividend && (
                  <Row gutter={16}>
                    {/* Expected Dividend */}
                    <Col xs={24} md={12}>
                      <Card size="small" style={{ backgroundColor: '#f0f5ff', border: '1px solid #adc6ff', height: '100%' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                            💰 예상 배당금
                          </Text>
                          <Statistic
                            value={expectedDividend.expectedAmount}
                            precision={0}
                            prefix="₩"
                            valueStyle={{ fontSize: '24px', color: '#52c41a', fontWeight: 'bold' }}
                          />
                          <Divider style={{ margin: '8px 0' }} />
                          <Space direction="vertical" size={0}>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px' }}>
                              <Text type="secondary">보유 수량:&nbsp;</Text>
                              <strong>{expectedDividend.totalQuantity.toLocaleString()}주</strong>
                              <Text type="secondary">&nbsp;(전체 계좌 합산)&nbsp;</Text>
                              {expectedDividend.quantityChange !== 0 ? (
                                <Text style={{ 
                                  color: expectedDividend.quantityChange > 0 ? '#52c41a' : '#ff4d4f',
                                  fontWeight: 'bold',
                                  marginLeft: '4px'
                                }}>
                                  ({expectedDividend.quantityChange > 0 ? '+' : ''}{expectedDividend.quantityChange.toLocaleString()}주)
                                </Text>
                              ) : (
                                <Text type="secondary" style={{ marginLeft: '4px' }}>(변화 없음)</Text>
                              )}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              최근 주당 배당금: <strong>₩{expectedDividend.lastDividendAmount.toLocaleString()}</strong>
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              기준일: {expectedDividend.lastDividendDate}
                            </Text>
                          </Space>
                        </Space>
                      </Card>
                    </Col>
                    
                    {/* Last Received Dividend */}
                    <Col xs={24} md={12}>
                      <Card size="small" style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591', height: '100%' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text strong style={{ fontSize: '14px', color: '#fa8c16' }}>
                            📊 마지막으로 받은 배당금 (월별 합산)
                          </Text>
                          <Statistic
                            value={expectedDividend.lastReceivedTotalAmount}
                            precision={0}
                            prefix="₩"
                            valueStyle={{ fontSize: '24px', color: '#fa8c16', fontWeight: 'bold' }}
                          />
                          <Divider style={{ margin: '8px 0' }} />
                          <Space direction="vertical" size={0}>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px' }}>
                              <Text type="secondary">수령 수량:&nbsp;</Text>
                              <strong>{expectedDividend.lastReceivedQuantity.toLocaleString()}주</strong>
                              <Text type="secondary">&nbsp;(전체 계좌 합산)</Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              배당 건수: <strong>{expectedDividend.lastReceivedRecordCount}건</strong>
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              주당 배당금: <strong>₩{expectedDividend.lastDividendAmount.toLocaleString()}</strong>
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              수령월: <strong>{expectedDividend.lastReceivedMonth}</strong>
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              계좌: <strong>{expectedDividend.lastReceivedAccount}</strong>
                            </Text>
                          </Space>
                        </Space>
                      </Card>
                    </Col>
                  </Row>
                )}
              </Space>
            </div>
            
            <div id="stock-dividend-chart">
              {stockChartData ? (
                <Chart
                  options={stockChartData.options}
                  series={stockChartData.series}
                  type="line"
                  height={400}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                  <BarChartOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <div>Select a stock above to view its dividend timeline</div>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Modal for detailed monthly data */}
      <Modal
        title={`Dividend Details for ${selectedMonth}`}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedMonth('');
          setSelectedMonthData(null);
          setMonthSelectValue(undefined);
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        {selectedMonthData && (
          <div>
            {/* Summary Statistics */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={8}>
                <Statistic
                  title="Total Amount"
                  value={selectedMonthData.reduce((sum, item) => sum + (parseFloat(item.dividend_amount) * parseInt(item.quantity)), 0)}
                  precision={0}
                  prefix="₩"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Records"
                  value={selectedMonthData.length}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Unique Stocks"
                  value={new Set(selectedMonthData.map(item => item.stock_symbol)).size}
                  valueStyle={{ color: '#eb2f96' }}
                />
              </Col>
            </Row>

            <Divider />

            {/* Overall Summary Section - By Stock */}
            <div style={{ marginBottom: '32px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <Title level={4} style={{ color: '#1890ff', marginBottom: '16px' }}>
                📈 By Stock - All Accounts Summary
              </Title>
              
              {(() => {
                // Calculate stock breakdown across all accounts
                const stockBreakdown = {};

                selectedMonthData.forEach(item => {
                  const dividendAmount = parseFloat(item.dividend_amount) || 0;
                  const quantity = parseInt(item.quantity) || 0;
                  const totalAmount = dividendAmount * quantity;
                  
                  const stockKey = `${item.stock_symbol} (${item.stock_name})`;
                  if (!stockBreakdown[stockKey]) {
                    stockBreakdown[stockKey] = {
                      totalAmount: 0,
                      perShareAmount: dividendAmount, // 주당 배당금은 첫 번째 값으로 설정
                      quantity: 0
                    };
                  }
                  stockBreakdown[stockKey].totalAmount += totalAmount;
                  // perShareAmount는 더하지 않고 그대로 유지 (같은 주식이므로 동일한 값)
                  stockBreakdown[stockKey].quantity += quantity;
                });

                return (
                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
                    <Table
                      columns={[
                        {
                          title: 'Stock',
                          dataIndex: 'stockName',
                          key: 'stockName',
                          width: '40%',
                          render: (stockName) => (
                            <span style={{ fontWeight: '500' }}>{stockName}</span>
                          )
                        },
                        {
                          title: 'Quantity',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          width: '15%',
                          align: 'right',
                          render: (quantity) => (
                            <span style={{ color: '#722ed1', fontWeight: 'bold' }}>
                              {quantity.toLocaleString()}
                            </span>
                          )
                        },
                        {
                          title: 'Per Share Amount',
                          dataIndex: 'perShareAmount',
                          key: 'perShareAmount',
                          width: '20%',
                          align: 'right',
                          render: (amount) => (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                              ₩{amount.toLocaleString()}
                            </span>
                          )
                        },
                        {
                          title: 'Total Amount',
                          dataIndex: 'totalAmount',
                          key: 'totalAmount',
                          width: '25%',
                          align: 'right',
                          render: (amount) => (
                            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                              ₩{amount.toLocaleString()}
                            </span>
                          )
                        }
                      ]}
                      dataSource={Object.entries(stockBreakdown)
                        .sort(([,a], [,b]) => b.totalAmount - a.totalAmount)
                        .map(([stockName, data], index) => ({
                          key: index,
                          stockName,
                          quantity: data.quantity,
                          perShareAmount: data.perShareAmount,
                          totalAmount: data.totalAmount
                        }))
                      }
                      pagination={false}
                      size="small"
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                );
              })()}
            </div>

            <Divider />

            {/* Group data by account */}
            {(() => {
              const accountGroups = {};
              selectedMonthData.forEach(item => {
                const accountName = item.account_name || 'Unknown Account';
                if (!accountGroups[accountName]) {
                  accountGroups[accountName] = [];
                }
                accountGroups[accountName].push(item);
              });

              return Object.entries(accountGroups).map(([accountName, accountData]) => (
                <div key={accountName} style={{ marginBottom: '32px' }}>
                  <Title level={4} style={{ color: '#1890ff', marginBottom: '16px' }}>
                    {accountName}
                    <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '16px', color: '#666' }}>
                      Total: ₩{accountData.reduce((sum, item) => sum + (parseFloat(item.dividend_amount) * parseInt(item.quantity)), 0).toLocaleString()}
                    </span>
                  </Title>
                  
                  <Table
                    columns={[
                      {
                        title: 'Date',
                        dataIndex: 'dividend_date',
                        key: 'dividend_date',
                        sorter: (a, b) => moment(a.dividend_date).unix() - moment(b.dividend_date).unix(),
                        render: (date) => moment(date).format('YYYY-MM-DD'),
                        width: 120
                      },
                      {
                        title: 'Stock Symbol',
                        dataIndex: 'stock_symbol',
                        key: 'stock_symbol',
                        width: 120
                      },
                      {
                        title: 'Stock Name',
                        dataIndex: 'stock_name',
                        key: 'stock_name',
                        ellipsis: true
                      },
                      {
                        title: 'Quantity',
                        dataIndex: 'quantity',
                        key: 'quantity',
                        sorter: (a, b) => a.quantity - b.quantity,
                        render: (quantity) => quantity.toLocaleString(),
                        width: 100,
                        align: 'right'
                      },
                      {
                        title: 'Amount (Per Share)',
                        dataIndex: 'dividend_amount',
                        key: 'dividend_amount',
                        sorter: (a, b) => parseFloat(a.dividend_amount) - parseFloat(b.dividend_amount),
                        render: (amount) => `₩${parseFloat(amount).toLocaleString('ko-KR')}`,
                        width: 140,
                        align: 'right'
                      },
                      {
                        title: 'Total Amount',
                        key: 'total_amount',
                        sorter: (a, b) => (parseFloat(a.dividend_amount) * parseInt(a.quantity)) - (parseFloat(b.dividend_amount) * parseInt(b.quantity)),
                        render: (_, record) => {
                          const totalAmount = parseFloat(record.dividend_amount) * parseInt(record.quantity);
                          return `₩${totalAmount.toLocaleString('ko-KR')}`;
                        },
                        width: 140,
                        align: 'right'
                      }
                    ]}
                    dataSource={accountData}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 800 }}
                    style={{ marginBottom: '16px' }}
                  />
                </div>
              ));
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dividend;
